import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import complianceService from "services/compliance";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import StatusBadge from "./StatusBadge";

const DEFAULT_PAGE_SIZE = 20;
const SORT_OPTIONS = [
  { value: "rank:desc", label: "Priority (High → Low)" },
  { value: "dueInDays:asc", label: "Due Soon" },
];

const ENTITY_TYPE_OPTIONS = [
  { value: "all", label: "All entities" },
  { value: "corporate", label: "Corporates" },
  { value: "company", label: "Companies" },
];

const isDev = process.env.NODE_ENV === "development";
const logDebug = (...args) => {
  if (isDev && typeof console !== "undefined" && console.debug) {
    // eslint-disable-next-line no-console
    console.debug("[OrgComplianceTasksList]", ...args);
  }
};

const OrgComplianceTasksList = ({ orgAdminId }) => {
  const [response, setResponse] = useState({
    data: [],
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const [entityType, setEntityType] = useState(ENTITY_TYPE_OPTIONS[0].value);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasOrgAdmin = Boolean(orgAdminId);

  useEffect(() => {
    setPage(1);
  }, [orgAdminId]);

  useEffect(() => {
    if (!hasOrgAdmin) {
      setResponse({ data: [], page: 1, limit, total: 0 });
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchTasks = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await complianceService.fetchOrgComplianceTasks({
          orgAdminId,
          page,
          limit,
          sort,
          entityType,
        });
        if (!isMounted) return;

        const normalized = {
          data: data?.data || [],
          page: data?.page || page,
          limit: data?.limit || limit,
          total: data?.total || 0,
        };

        setResponse(normalized);
        logDebug("Org tasks payload received", normalized);
      } catch (requestError) {
        if (!isMounted) return;
        setError(
          requestError?.message ||
            "We couldn't fetch the compliance tasks at the moment."
        );
        logDebug("Org tasks fetch failed", requestError);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTasks();

    return () => {
      isMounted = false;
    };
  }, [entityType, hasOrgAdmin, limit, orgAdminId, page, sort]);

  const rows = useMemo(() => response.data || [], [response.data]);
  const totalPages = useMemo(() => {
    if (!response.limit) return 1;
    return Math.max(1, Math.ceil((response.total || 0) / response.limit));
  }, [response.limit, response.total]);

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  const handleEntityTypeChange = (event) => {
    setEntityType(event.target.value);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-800">
            Aggregated task queue
          </h4>
          <p className="text-sm text-gray-600">
            Tasks where you are the registrar or default registrar across all
            assigned entities.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Sort
            </span>
            <select
              value={sort}
              onChange={handleSortChange}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-sflPrimary focus:outline-none"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Entity Type
            </span>
            <select
              value={entityType}
              onChange={handleEntityTypeChange}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-sflPrimary focus:outline-none"
            >
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Page Size
            </span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-sflPrimary focus:outline-none"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {!hasOrgAdmin && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-600">
          OrgAdmin profile not available yet. Assign a registrar to start
          tracking tasks.
        </div>
      )}

      {hasOrgAdmin && (
        <section className="space-y-4">
          {loading && (
            <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-gray-200 bg-white">
              <SpinnerIcon className="h-6 w-6 animate-spin text-sflPrimary" />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
              No tasks matched the selected filters.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-4 border-b border-gray-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <div>Entity</div>
                <div>Task</div>
                <div>Priority</div>
                <div>Due</div>
                <div className="text-right">Action</div>
              </div>
              <ul className="divide-y divide-gray-100">
                {rows.map((task) => (
                  <li
                    key={task.taskId || `${task.entityId}-${task.rank}`}
                    className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-4 px-4 py-4 text-sm text-gray-700"
                  >
                    <div className="space-y-1">
                      <p className="font-semibold text-gray-900">
                        {task.entityName || task.entityId}
                      </p>
                      {typeof task.stateBadge === "string" &&
                      task.stateBadge ? (
                        <StatusBadge label={task.stateBadge} />
                      ) : null}
                      {task.entityType && (
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          {task.entityType}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className="font-semibold text-gray-800">
                        {task.taskTitle || task.taskId}
                      </p>
                      {task.cta?.description && (
                        <p className="text-xs text-gray-500">
                          {task.cta.description}
                        </p>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {task.rank != null ? Number(task.rank).toFixed(3) : "—"}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDue(task.dueInDays, task.dueDate)}
                    </div>
                    <div className="flex items-center justify-end">
                      <TaskLink cta={task.cta} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <PaginationControls
              page={response.page}
              totalPages={totalPages}
              onChange={setPage}
            />
          )}
        </section>
      )}
    </div>
  );
};

OrgComplianceTasksList.propTypes = {
  orgAdminId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

OrgComplianceTasksList.defaultProps = {
  orgAdminId: null,
};

const PaginationControls = ({ page, totalPages, onChange }) => (
  <div className="flex items-center justify-end gap-3">
    <button
      type="button"
      onClick={() => onChange(Math.max(1, page - 1))}
      disabled={page <= 1}
      className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-60"
    >
      Previous
    </button>
    <span className="text-sm text-gray-600">
      Page {page} of {totalPages}
    </span>
    <button
      type="button"
      onClick={() => onChange(Math.min(totalPages, page + 1))}
      disabled={page >= totalPages}
      className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-60"
    >
      Next
    </button>
  </div>
);

PaginationControls.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const TaskLink = ({ cta }) => {
  if (!cta) {
    return (
      <button
        type="button"
        disabled
        className="cursor-not-allowed rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-400"
      >
        No action
      </button>
    );
  }

  if (typeof cta === "string") {
    return (
      <a
        href={cta}
        className="inline-flex items-center rounded-md bg-sflPrimary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90"
      >
        Open
      </a>
    );
  }

  const destination = cta.url || cta.href;

  if (!destination) {
    return (
      <button
        type="button"
        disabled
        className="cursor-not-allowed rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-400"
      >
        No action
      </button>
    );
  }

  return (
    <a
      href={destination}
      className="inline-flex items-center rounded-md bg-sflPrimary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-sflPrimary/90"
    >
      {cta.label || "Open"}
    </a>
  );
};

TaskLink.propTypes = {
  cta: PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
    href: PropTypes.string,
    description: PropTypes.string,
  }),
};

TaskLink.defaultProps = {
  cta: null,
};

const formatDue = (dueInDays, dueDate) => {
  if (dueInDays == null) {
    if (dueDate) {
      return formatNairobiDate(dueDate);
    }
    return "—";
  }

  const parsed = Number(dueInDays);
  if (Number.isNaN(parsed)) {
    return "—";
  }

  if (parsed === 0) return "Due today";
  if (parsed > 0) {
    return `Due in ${parsed} day${parsed === 1 ? "" : "s"}`;
  }
  const absolute = Math.abs(parsed);
  return `Overdue by ${absolute} day${absolute === 1 ? "" : "s"}`;
};

const formatNairobiDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString("en-GB", {
    timeZone: "Africa/Nairobi",
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

export default OrgComplianceTasksList;
